import { useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { message, Spin } from "antd";
import { useCookies } from "react-cookie";
import {
    getSessionDataFromAccessResponse,
    impersonateUser,
} from "components/login/login.api";
import {
    ADMIN_SESSION_KEY,
    defaultCookieOptions,
    SESSION_KEY,
} from "common/constants";
import { addRecentBrand } from "common/recentBrands";
import { useGetAdminSession } from "hooks/useGetUserSession";

/**
 * URL-addressable "login as brand" route (PROD-1989).
 *
 * Reachable at /brands/:userId/login. When an authenticated admin hits this
 * route it runs the impersonation flow and full-page-redirects into the Brand
 * Portal. Unauthenticated visitors are routed through Google SSO by RequireAuth
 * and resume here automatically after auth.
 *
 * Unlike LoginAsButton's handler, a failed impersonation here does NOT sign the
 * admin out — it surfaces an error and returns them to the brand profile.
 */
export const BrandLoginRedirect = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const adminAppSession = useGetAdminSession();
    const [, setCookies] = useCookies([SESSION_KEY]);
    const hasRun = useRef(false);

    useEffect(() => {
        // Guard against React StrictMode double-invoke in dev.
        if (hasRun.current) {
            return;
        }
        hasRun.current = true;

        impersonateUser(userId).then((response) => {
            const { success, result: accessToken } = response || {};

            if (!success) {
                message.error(
                    "Could not log in as this brand. Please try again.",
                );
                navigate(`/brands/${userId}/profile`, { replace: true });
                return;
            }

            addRecentBrand(userId);

            const data = getSessionDataFromAccessResponse(accessToken);
            setCookies(SESSION_KEY, data, { ...defaultCookieOptions });
            setCookies(ADMIN_SESSION_KEY, adminAppSession, {
                ...defaultCookieOptions,
            });

            window.location = process.env.REACT_APP_BP_BASE_URL;
        });
    }, [userId, adminAppSession, navigate, setCookies]);

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 12,
                height: "60vh",
            }}>
            <Spin />
            <span>Logging you in…</span>
        </div>
    );
};
